import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight, Clock } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { useLang } from "@/context/LanguageContext";
import { useSEO } from "@/lib/useSEO";
import { ATMOSPHERIC_IMAGES } from "@/lib/api";

const EXPERIENCES = [
  {
    id: "roccaraso",
    cat: "nature",
    img: "https://customer-assets.emergentagent.com/job_premium-retreats-3/artifacts/5nzik433_neve.webp",
    distance: "12 km",
    title_it: "Piste da sci di Roccaraso-Aremogna",
    title_en: "Roccaraso-Aremogna ski slopes",
    desc_it: "Il più grande comprensorio sciistico dell'Appennino centro-meridionale: 110 km di piste tra Roccaraso, Aremogna, Rivisondoli e Pescocostanzo. Ideale per famiglie, sciatori esperti e snowboarder.",
    desc_en: "The largest ski area in central-southern Apennines: 110 km of slopes across Roccaraso, Aremogna, Rivisondoli and Pescocostanzo. Perfect for families, expert skiers and snowboarders.",
  },
  {
    id: "parco",
    cat: "nature",
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_premium-retreats-3/artifacts/yue0e8sg_parco-nazionale-d-abruzzo.jpg",
    distance: "20 km",
    title_it: "Parco Nazionale d'Abruzzo",
    title_en: "Abruzzo National Park",
    desc_it: "Uno dei parchi più antichi d'Italia. Casa dell'orso marsicano, del camoscio d'Abruzzo e del lupo appenninico. Faggete secolari, panorami mozzafiato ed escursioni per ogni livello.",
    desc_en: "One of Italy's oldest national parks. Home to the Marsican brown bear, Apennine chamois and grey wolf. Ancient beech forests, breathtaking views and hikes for every level.",
  },
  {
    id: "barrea",
    cat: "nature",
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_premium-retreats-3/artifacts/9r9f61ea_CANOA%20BARREA.jpg",
    distance: "28 km",
    title_it: "Lago di Barrea",
    title_en: "Lake Barrea",
    desc_it: "Uno specchio d'acqua turchese incastonato tra le montagne del Parco Nazionale, con il borgo di Barrea che si affaccia dall'alto come un presepe. Ora ufficialmente balneabile: nei mesi estivi puoi fare il bagno, prendere il sole sulle sponde e goderti l'acqua cristallina di alta quota. Perfetto anche per passeggiate a piedi o in bici lungo il perimetro, canoa, kayak, SUP e picnic in famiglia.",
    desc_en: "A turquoise mirror of water nestled among the National Park mountains, with the village of Barrea overlooking it from above. Now officially open for swimming: in summer you can bathe, sunbathe on the shores and enjoy the crystal-clear high-altitude water. Perfect also for walks or cycling around the lake, canoeing, kayaking, SUP and family picnics.",
  },
  {
    id: "valfondillo",
    cat: "nature",
    img: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=940&q=80&auto=format&fit=crop",
    distance: "35 km",
    title_it: "Val Fondillo · Camosciara",
    title_en: "Val Fondillo · Camosciara",
    desc_it: "Il cuore selvaggio del Parco Nazionale. Sentieri tra faggete, cascate, torrenti cristallini e la possibilità di avvistare cervi e camosci al tramonto.",
    desc_en: "The wild heart of the National Park. Trails through beech forests, waterfalls, crystal-clear streams, and the chance to spot deer and chamois at sunset.",
  },
  {
    id: "reale",
    cat: "food",
    img: "https://images.pexels.com/photos/262978/pexels-photo-262978.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=940",
    distance: "1 km",
    title_it: "Reale · Niko Romito (3 stelle Michelin)",
    title_en: "Reale · Niko Romito (3 Michelin stars)",
    desc_it: "A pochi passi dalla dimora, uno dei ristoranti più celebrati al mondo. Cucina italiana essenziale portata al livello dell'arte da Niko Romito. Prenotazione con largo anticipo.",
    desc_en: "A few steps from the retreat, one of the most celebrated restaurants in the world. Essential Italian cuisine elevated to art by Niko Romito. Reservations well in advance.",
  },
  {
    id: "cucina_locale",
    cat: "food",
    img: "https://images.pexels.com/photos/958545/pexels-photo-958545.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=940",
    distance: "centro",
    title_it: "Sapori d'Abruzzo",
    title_en: "Abruzzo flavours",
    desc_it: "Trattorie e osterie del centro storico dove assaggiare arrosticini, maccheroni alla chitarra, agnello cacio e uova, formaggi di pecora e vini locali (Montepulciano, Trebbiano).",
    desc_en: "Trattorias and osterias in the historic centre where you can taste arrosticini skewers, chitarra pasta, lamb with cheese and eggs, sheep cheeses and local wines (Montepulciano, Trebbiano).",
  },
  {
    id: "basilica",
    cat: "culture",
    img: "https://images.pexels.com/photos/2110099/pexels-photo-2110099.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=940",
    distance: "centro",
    title_it: "Basilica di Santa Maria Assunta",
    title_en: "Basilica of Santa Maria Assunta",
    desc_it: "La chiesa madre di Castel di Sangro, monumentale esempio di barocco abruzzese. Cupola imponente, interni riccamente decorati e vista panoramica sul borgo.",
    desc_en: "The mother church of Castel di Sangro, a monumental example of Abruzzo baroque. Imposing dome, richly decorated interiors and panoramic views of the village.",
  },
  {
    id: "convento",
    cat: "culture",
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_premium-retreats-3/artifacts/a5i8xb5o_castel-di-sangro-la-maddalena.webp",
    distance: "centro",
    title_it: "Convento della Maddalena · MAM",
    title_en: "Maddalena Convent · MAM",
    desc_it: "Convento storico che ospita il Museo Civico Aufidenate (MAM) con reperti archeologici sanniti e romani, oltre a mostre d'arte contemporanea.",
    desc_en: "Historic convent hosting the Aufidenate Civic Museum (MAM) with Samnite and Roman archaeological findings, plus contemporary art exhibitions.",
  },
  {
    id: "pescocostanzo",
    cat: "villages",
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_premium-retreats-3/artifacts/cq5oe2qz_pescocostanzo-hd.jpg",
    distance: "15 km",
    title_it: "Pescocostanzo · Borgo tra i più belli d'Italia",
    title_en: "Pescocostanzo · One of Italy's most beautiful villages",
    desc_it: "Borgo medievale intatto, insignito della bandiera arancione del Touring Club. Ammira la Collegiata di Santa Maria del Colle, le botteghe di merletto e le case in pietra.",
    desc_en: "Perfectly preserved medieval village, awarded the Touring Club's orange flag. Admire the Collegiate of Santa Maria del Colle, lace workshops and stone houses.",
  },
  {
    id: "scanno",
    cat: "villages",
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_premium-retreats-3/artifacts/kfthwq1l_lago-scanno-cuore-.jpg",
    distance: "45 km",
    title_it: "Scanno · Il lago a forma di cuore",
    title_en: "Scanno · The heart-shaped lake",
    desc_it: "Uno dei borghi più fotografati d'Italia, con il celebre lago a forma di cuore. Antiche vie, orafi e sarte custodi di tradizioni secolari, panorami da cartolina.",
    desc_en: "One of the most photographed villages in Italy, with its famous heart-shaped lake. Ancient streets, goldsmiths and dressmakers keeping centuries-old traditions, postcard views.",
  },
  {
    id: "sulmona",
    cat: "villages",
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_premium-retreats-3/artifacts/3onzcqmj_treno%20sulmona.jfif",
    distance: "stazione",
    title_it: "Transiberiana d'Italia · Sulmona-Castel di Sangro",
    title_en: "Trans-Siberian of Italy · Sulmona-Castel di Sangro",
    desc_it: "Una delle ferrovie panoramiche più belle d'Europa. Lo storico treno d'epoca collega Sulmona a Castel di Sangro attraversando l'altopiano delle Cinque Miglia, Roccaraso e i paesaggi mozzafiato dell'Appennino: 128 km tra faggete, viadotti e vette innevate. Corse a tema durante tutto l'anno (foliage, mercatini, neve). Sali a bordo direttamente dalla stazione di Castel di Sangro, a pochi minuti dalla dimora — a Sulmona ti attendono la città natale di Ovidio, Piazza Garibaldi, l'acquedotto medievale e le fabbriche storiche di confetti Pelino.",
    desc_en: "One of Europe's most scenic railways. The historic vintage train links Sulmona to Castel di Sangro crossing the Cinque Miglia plateau, Roccaraso and the breathtaking Apennine landscapes: 128 km through beech forests, viaducts and snow-capped peaks. Themed rides all year round (foliage, Christmas markets, snow). Board directly from Castel di Sangro station, minutes from the retreat — in Sulmona you'll find Ovid's birthplace, Garibaldi Square, the medieval aqueduct and the historic Pelino sugared-almonds factories.",
  },
  {
    id: "arrosticini",
    cat: "food",
    img: "https://images.pexels.com/photos/1435904/pexels-photo-1435904.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=940",
    distance: "vari",
    title_it: "Rifugi e ristori di montagna",
    title_en: "Mountain refuges and huts",
    desc_it: "Dopo un'escursione, fermatevi in un rifugio di montagna per assaggiare arrosticini alla brace, salumi tipici e caciocavallo. Un rituale imperdibile dell'esperienza abruzzese.",
    desc_en: "After a hike, stop at a mountain refuge to taste grilled arrosticini, local cured meats and caciocavallo cheese. An unmissable ritual of the Abruzzo experience.",
  },
  {
    id: "acquapark",
    cat: "services",
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_premium-retreats-3/artifacts/41v4f6kh_acquapark%20castel%20di%20sangro.webp",
    distance: "centro",
    title_it: "Acquapark · Divertimento estivo",
    title_en: "Waterpark · Summer fun",
    desc_it: "Nel cuore di Castel di Sangro, il parco acquatico è la meta perfetta per famiglie e bambini nei mesi estivi (giugno-settembre). Piscine, scivoli e area relax. Chiedeteci info aggiornate su orari e tariffe al check-in.",
    desc_en: "In the heart of Castel di Sangro, the waterpark is the perfect destination for families and children in the summer months (June-September). Pools, slides and a relax area. Ask us for updated hours and prices at check-in.",
  },
  {
    id: "ciclabile",
    cat: "services",
    img: "https://images.pexels.com/photos/276517/pexels-photo-276517.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=940",
    distance: "5 min",
    title_it: "Pista ciclabile · Noleggio bici",
    title_en: "Bike path · Bike rental",
    desc_it: "Una splendida pista ciclabile immersa nella natura, lungo il fiume Sangro. Perfetta per famiglie, coppie e appassionati. Possibilità di noleggio bici in paese: consegna anche direttamente in struttura su richiesta.",
    desc_en: "A stunning bike path immersed in nature along the Sangro river. Perfect for families, couples and cycling enthusiasts. Bike rental available in the village: on-site delivery available upon request.",
  },
  {
    id: "alex_saloon",
    cat: "services",
    featured: true,
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_premium-retreats-3/artifacts/0o1uc1hx_alex%20saloon.jpg",
    distance: "centro",
    title_it: "Alex Saloon · Cocktail Bar & Ginneria",
    title_en: "Alex Saloon · Cocktail Bar & Gin Bar",
    desc_it: "Il nostro locale del cuore a Castel di Sangro. Un cocktail bar & ginneria dall'atmosfera raffinata e informale, con una selezione curata di gin d'autore, cocktail signature e proposte per gli aperitivi. Ambiente accogliente, servizio attento e la giusta cornice per iniziare — o chiudere — la serata in bellezza. Consigliato di cuore.",
    desc_en: "Our favourite spot in Castel di Sangro. A refined yet informal cocktail bar & gin bar, with a curated selection of premium gins, signature cocktails and aperitivo offerings. Warm atmosphere, attentive service and the perfect setting to start — or end — your evening in style. Warmly recommended.",
  },
  {
    id: "mancini",
    cat: "services",
    img: "https://images.pexels.com/photos/848599/pexels-photo-848599.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=940",
    distance: "centro",
    title_it: "Store Mancini · Noleggio & vendita",
    title_en: "Mancini Store · Rental & sales",
    desc_it: "Storico punto di riferimento a Castel di Sangro per lo sport outdoor. In inverno: noleggio e vendita sci, snowboard, attacchi, scarponi e abbigliamento tecnico. In estate: biciclette per la pista ciclabile e le uscite in montagna. Assistenza professionale.",
    desc_en: "The historic reference point in Castel di Sangro for outdoor sports. In winter: rental and sale of skis, snowboards, bindings, boots and technical clothing. In summer: bicycles for the bike path and mountain rides. Professional assistance.",
  },
  {
    id: "pesca",
    cat: "services",
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_premium-retreats-3/artifacts/5fga727d_lago%20castel%20di%20sangro.jpg",
    distance: "10 min",
    title_it: "Lago di pesca sportiva",
    title_en: "Sport fishing lake",
    desc_it: "Un'oasi tranquilla per gli appassionati di pesca sportiva, con lo specchio d'acqua che riflette le vette innevate. Ideale per una giornata rilassante o per iniziare i bambini a questa disciplina. Attrezzatura noleggiabile in loco.",
    desc_en: "A peaceful oasis for sport fishing lovers, with a mirror of water reflecting the snow-capped peaks. Perfect for a relaxing day or to introduce children to this discipline. Equipment available for rent on site.",
  },
  {
    id: "tennis_fit",
    cat: "services",
    img: "https://images.pexels.com/photos/342361/pexels-photo-342361.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=940",
    distance: "centro",
    title_it: "Centro Estivo FIT · Tennis",
    title_en: "FIT Summer Camp · Tennis",
    desc_it: "Uno dei centri estivi più importanti d'Italia della Federazione Italiana Tennis. Corsi, stage e camp intensivi per ragazzi e adulti nel periodo estivo. Circoli e campi in terra rossa a pochi minuti dalla dimora.",
    desc_en: "One of Italy's most important summer camps run by the Italian Tennis Federation (FIT). Courses, clinics and intensive camps for children and adults in summer. Clubs and clay courts a few minutes from the retreat.",
  },
  {
    id: "stadio_napoli",
    cat: "services",
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_premium-retreats-3/artifacts/6zfxoe3z_napoli.jpg",
    distance: "5 min",
    title_it: "Stadio Patini · Ritiro SSC Napoli",
    title_en: "Patini Stadium · SSC Napoli training camp",
    desc_it: "Ogni estate lo Stadio Teofilo Patini di Castel di Sangro ospita il ritiro precampionato del SSC Napoli, campione d'Italia. Un'occasione unica per tifosi e curiosi: allenamenti aperti al pubblico, autografi e amichevoli di livello internazionale.",
    desc_en: "Every summer Stadio Teofilo Patini in Castel di Sangro hosts the pre-season training camp of SSC Napoli, Italian champions. A unique opportunity for fans: open training sessions, autographs and international friendly matches.",
  },
];

const ExperiencePage = () => {
  const { lang } = useLang();
  const [filter, setFilter] = useState("all");

  const CATEGORIES = [
    { key: "all", label: lang === "en" ? "All" : "Tutte" },
    { key: "nature", label: lang === "en" ? "Nature & Hiking" : "Natura & Escursioni" },
    { key: "food", label: lang === "en" ? "Food & Wine" : "Sapori" },
    { key: "culture", label: lang === "en" ? "Culture & Monuments" : "Cultura & Monumenti" },
    { key: "villages", label: lang === "en" ? "Nearby Villages" : "Borghi vicini" },
    { key: "services", label: lang === "en" ? "Services & Activities" : "Servizi & Attività" },
  ];

  useSEO({
    title: lang === "en" ? "What to do in Castel di Sangro" : "Cosa fare a Castel di Sangro",
    description: lang === "en"
      ? "Discover the best of Castel di Sangro and the Abruzzo Apennines: Roccaraso ski slopes, Abruzzo National Park, Lake Barrea, Michelin-star dining and medieval villages nearby."
      : "Scopri il meglio di Castel di Sangro e dell'Appennino abruzzese: piste di Roccaraso, Parco Nazionale d'Abruzzo, Lago di Barrea, ristoranti stellati e borghi medievali.",
    keywords: "cosa fare Castel di Sangro, attrazioni Roccaraso, escursioni Parco Nazionale d'Abruzzo, ristoranti Castel di Sangro, Niko Romito Reale, borghi Abruzzo, Pescocostanzo Scanno",
  });

  const items = filter === "all"
    ? [...EXPERIENCES].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0))
    : EXPERIENCES.filter((e) => e.cat === filter).sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));

  return (
    <main data-testid="experience-page" className="pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal className="text-center mb-14">
          <div className="overline text-accent gold-line-center mb-6">
            {lang === "en" ? "Explore" : "Esplora"}
          </div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-tight">
            {lang === "en" ? "What to do in " : "Cosa fare a "}<em className="text-accent not-italic">Castel di Sangro.</em>
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto mt-6">
            {lang === "en"
              ? "A hand-picked selection of experiences to make your stay unforgettable — from Michelin-star tables to medieval villages, from ski slopes to national park trails."
              : "Una selezione curata di esperienze per rendere indimenticabile il tuo soggiorno — dalle tavole stellate ai borghi medievali, dalle piste da sci ai sentieri del Parco Nazionale."}
          </p>
        </Reveal>

        <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-14">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              data-testid={`exp-filter-${c.key}`}
              onClick={() => setFilter(c.key)}
              className={`px-5 py-2 rounded-full text-sm border transition-colors ${
                filter === c.key
                  ? "bg-foreground text-background border-foreground"
                  : "border-border text-muted-foreground hover:border-accent hover:text-accent"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {items.map((e, i) => (
            <Reveal key={e.id} delay={i * 60}>
              <article className={`group h-full bg-card border rounded-2xl overflow-hidden transition-colors ${
                e.featured
                  ? "border-accent shadow-lg shadow-accent/10 ring-1 ring-accent/40"
                  : "border-border hover:border-accent/50"
              }`}>
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img
                    src={e.img}
                    alt={lang === "en" ? e.title_en : e.title_it}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  {e.featured && (
                    <div className="absolute top-3 left-3 bg-accent text-accent-foreground text-xs font-medium px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-md">
                      <span>★</span>
                      <span className="tracking-wide">{lang === "en" ? "OUR PICK" : "CONSIGLIATO"}</span>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="overline text-accent">
                      {CATEGORIES.find((c) => c.key === e.cat)?.label}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin size={12} className="text-accent" />
                      {e.distance}
                    </div>
                  </div>
                  <h3 className="font-serif text-2xl mb-3 leading-tight">
                    {lang === "en" ? e.title_en : e.title_it}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {lang === "en" ? e.desc_en : e.desc_it}
                  </p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-24 text-center">
          <div className="overline text-accent gold-line-center mb-6">
            {lang === "en" ? "Ready?" : "Pronti?"}
          </div>
          <h2 className="font-serif text-4xl md:text-5xl mb-8">
            {lang === "en" ? "Base yourself at the heart of it all." : "Il vostro punto di partenza."}
          </h2>
          <Link
            to="/booking"
            data-testid="exp-cta-book"
            className="btn-shimmer inline-flex items-center gap-3 px-12 py-4 rounded-full bg-foreground text-background hover:bg-accent transition-colors text-sm tracking-wide"
          >
            {lang === "en" ? "BOOK YOUR STAY" : "PRENOTA IL TUO SOGGIORNO"} <ArrowRight size={16} />
          </Link>
        </Reveal>
      </div>
    </main>
  );
};

export default ExperiencePage;
